import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { API_BASE, getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';
import { SUBCATEGORIES } from './project-steps/constants';

interface DefaultLeadImage {
    _id: string;
    propertyType: string;
    subCategory: string;
    imageUrl: string;
    createdAt?: string;
}

export default function AdminDefaultLeadImages() {
    const { token } = useAuth();
    const [images, setImages] = useState<DefaultLeadImage[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Modal & editing state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingImage, setEditingImage] = useState<DefaultLeadImage | null>(null);

    // Form inputs state
    const typeOptions = Object.keys(SUBCATEGORIES);
    const [propertyType, setPropertyType] = useState(typeOptions[0]);
    const [subCategory, setSubCategory] = useState(SUBCATEGORIES[typeOptions[0]][0]);
    const [imageUrl, setImageUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchDefaultImages = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/default-lead-images`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setImages(data);
            }
        } catch (err) {
            console.error('Failed to fetch default lead images', err);
            toast.error('Failed to load default images');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchDefaultImages();
        }
    }, [token]);

    // Handle property type change to reset sub category
    useEffect(() => {
        const validSubs = SUBCATEGORIES[propertyType] || [];
        if (!validSubs.includes(subCategory) && validSubs.length > 0) {
            setSubCategory(validSubs[0]);
        }
    }, [propertyType]);

    const openAddModal = () => {
        setEditingImage(null);
        setPropertyType(typeOptions[0]);
        setSubCategory(SUBCATEGORIES[typeOptions[0]][0]);
        setImageUrl('');
        setIsModalOpen(true);
    };

    const openEditModal = (img: DefaultLeadImage) => {
        setEditingImage(img);
        setImageUrl(img.imageUrl);
        setPropertyType(img.propertyType);
        setSubCategory(img.subCategory);
        setIsModalOpen(true);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('images', file);
        
        setUploading(true);
        try {
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (res.ok && data.urls && data.urls.length > 0) {
                setImageUrl(data.urls[0]);
                toast.success('Image uploaded successfully');
            } else {
                toast.error(data.message || 'Image upload failed');
            }
        } catch (err) {
            console.error('Upload failed', err);
            toast.error('Image upload failed due to server error');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const finalType = propertyType;
        const finalSub = subCategory;

        if (!finalType || !finalSub || !imageUrl) {
            toast.error('Please configure property type, sub category, and upload an image.');
            return;
        }

        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/default-lead-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    propertyType: finalType,
                    subCategory: finalSub,
                    imageUrl
                })
            });

            if (res.ok) {
                toast.success(editingImage ? 'Default lead image updated' : 'Default lead image created');
                setIsModalOpen(false);
                fetchDefaultImages();
            } else {
                const data = await res.json();
                toast.error(data.message || 'Failed to save default image');
            }
        } catch (err) {
            console.error('Failed to save', err);
            toast.error('Server error saving default image');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this default lead image? Classifieds matching these types will fall back to site-wide default image.')) return;

        try {
            const res = await fetch(`${API_BASE}/default-lead-images/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success('Deleted successfully');
                setImages(prev => prev.filter(img => img._id !== id));
            } else {
                const data = await res.json();
                toast.error(data.message || 'Failed to delete');
            }
        } catch (err) {
            console.error('Delete failed', err);
            toast.error('Connection error while deleting');
        }
    };

    return (
        <AdminLayout>
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-[#05144b] mb-1.5 leading-none">Default Lead Images</h1>
                    <p className="text-slate-500 font-semibold text-sm">Configure default cover images for classified properties based on their type and subtype.</p>
                </div>
                <button 
                    onClick={openAddModal}
                    className="px-6 py-3.5 bg-gradient-to-r from-[var(--purple)] to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-extrabold rounded-2xl shadow-lg hover:shadow-purple-100 transition-all flex items-center gap-2 active:scale-95 text-sm uppercase tracking-wider"
                >
                    <i className="fa-solid fa-circle-plus text-base"></i> Add Default Image
                </button>
            </div>

            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100 min-h-[500px]">
                <h3 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                    <i className="fa-solid fa-list-check text-[var(--purple)]"></i> Configured Default Images
                </h3>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 gap-3">
                        <div className="w-12 h-12 border-4 border-[var(--purple)] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-slate-400 font-bold text-sm">Loading default images...</p>
                    </div>
                ) : images.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center">
                        <div className="w-16 h-16 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mb-4 text-2xl">
                            <i className="fa-regular fa-image"></i>
                        </div>
                        <h4 className="text-base font-bold text-slate-700">No Default Images Found</h4>
                        <p className="text-slate-400 text-xs mt-1 font-semibold max-w-xs mx-auto">Create configurations by uploading lead cover images for type & subtype combinations.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {images.map((img) => (
                            <div key={img._id} className="group bg-slate-50/50 hover:bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300">
                                <div>
                                    <div className="relative aspect-video bg-black overflow-hidden">
                                        <img 
                                            src={getImageUrl(img.imageUrl)} 
                                            alt={`${img.propertyType} - ${img.subCategory}`}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-95"
                                        />
                                        <div className="absolute top-3 right-3 flex gap-2">
                                            <button 
                                                onClick={() => openEditModal(img)}
                                                className="w-8 h-8 rounded-full bg-white hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 flex items-center justify-center shadow-md transition-all active:scale-95"
                                                title="Edit"
                                            >
                                                <i className="fa-solid fa-pen text-xs"></i>
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(img._id)}
                                                className="w-8 h-8 rounded-full bg-white hover:bg-red-50 text-slate-500 hover:text-red-600 flex items-center justify-center shadow-md transition-all active:scale-95"
                                                title="Delete"
                                            >
                                                <i className="fa-solid fa-trash-can text-xs"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        <div className="flex flex-wrap gap-2">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black bg-indigo-50 text-indigo-600 uppercase tracking-wider">
                                                {img.propertyType}
                                            </span>
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black bg-purple-50 text-[var(--purple)] uppercase tracking-wider">
                                                {img.subCategory}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Premium Dialog Box / Modal Overlay */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate__animated animate__fadeIn">
                    <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] animate__animated animate__zoomIn animate__faster">
                        {/* Header */}
                        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                                <i className={`fa-solid ${editingImage ? 'fa-pen' : 'fa-circle-plus'} text-[var(--purple)]`}></i>
                                {editingImage ? 'Edit Default Image' : 'Add Default Image'}
                            </h3>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors shadow-sm"
                            >
                                <i className="fa-solid fa-times text-sm"></i>
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-5">
                            {/* Property Type Choice */}
                            <div>
                                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Property Type</label>
                                <select 
                                    value={propertyType} 
                                    onChange={(e) => setPropertyType(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-bold text-slate-800 text-sm focus:border-[var(--purple)] transition-colors"
                                >
                                    {typeOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Sub Category Choice */}
                            <div>
                                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Sub Category</label>
                                <select 
                                    value={subCategory} 
                                    onChange={(e) => setSubCategory(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-bold text-slate-800 text-sm focus:border-[var(--purple)] transition-colors"
                                >
                                    {(SUBCATEGORIES[propertyType] || []).map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Image Upload */}
                            <div>
                                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Lead Cover Image</label>
                                {imageUrl ? (
                                    <div className="relative rounded-2xl overflow-hidden aspect-video border border-slate-200 bg-slate-50">
                                        <img 
                                            src={getImageUrl(imageUrl)} 
                                            alt="Preview" 
                                            className="w-full h-full object-cover"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setImageUrl('')} 
                                            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-md transition-all active:scale-95"
                                        >
                                            <i className="fa-solid fa-trash text-xs"></i>
                                        </button>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed border-slate-200 hover:border-[var(--purple)] rounded-2xl cursor-pointer bg-slate-50/50 hover:bg-slate-50 transition-all group">
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            {uploading ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-8 h-8 border-4 border-[var(--purple)] border-t-transparent rounded-full animate-spin"></div>
                                                    <span className="text-xs font-bold text-slate-400">Uploading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-cloud-arrow-up text-3xl text-slate-400 group-hover:scale-110 transition-transform mb-3"></i>
                                                    <p className="text-xs font-bold text-slate-500">Click to upload cover image</p>
                                                    <p className="text-[10px] text-slate-400 mt-1 font-semibold">PNG, JPG, WEBP up to 10MB</p>
                                                </>
                                            )}
                                        </div>
                                        <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                                    </label>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="pt-4 flex gap-3 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-5 py-3 border-2 border-slate-200 hover:bg-slate-50 text-slate-700 font-extrabold rounded-xl text-xs uppercase tracking-wider transition-colors active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || uploading}
                                    className="flex-1 bg-[#05144b] hover:bg-[#030c2d] text-white font-extrabold py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-save"></i> Save Config
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
